import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Circle as CircleIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';

import { DS, Overline, numericSx } from '../shared/Dashboard';

/*****************************************************************************
 ** Month calendar for events. All dates are UTC, matching how event times
 ** are stored and shown elsewhere in admin.
*****************************************************************************/

export interface CalendarEvent {
  id: string;
  name: string;
  start: moment.Moment;
  end: moment.Moment;
  color: string;
  statusLabel: string;
  onClick?: () => void;
}

interface EventCalendarProps {
  events: CalendarEvent[];
  legend: { label: string; color: string }[];
}

interface DayEvent extends CalendarEvent {
  firstDay: moment.Moment;
  lastDay: moment.Moment;
}

interface Segment {
  event: DayEvent;
  startCol: number;
  span: number;
  lane: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MAX_LANES = 3;
const DAY_HEADER_HEIGHT = 30;
const LANE_HEIGHT = 22;
const MORE_ROW_HEIGHT = 18;

const formatRange = (event: CalendarEvent) =>
  `${event.start.format('MMM D, HH:mm')} – ${event.end.format('MMM D, HH:mm')} UTC`;

const toDayEvent = (event: CalendarEvent): DayEvent => {
  // An event ending exactly at midnight does not occupy that day
  const lastMoment = event.end.isAfter(event.start) ? event.end.clone().subtract(1, 'ms') : event.start;
  return { ...event, firstDay: event.start.clone().startOf('day'), lastDay: lastMoment.clone().startOf('day') };
};

const buildWeeks = (month: moment.Moment): moment.Moment[][] => {
  const day = month.clone().startOf('month').startOf('isoWeek');
  const gridEnd = month.clone().endOf('month').endOf('isoWeek');
  const weeks: moment.Moment[][] = [];

  while (day.isBefore(gridEnd)) {
    const week: moment.Moment[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(day.clone());
      day.add(1, 'day');
    }
    weeks.push(week);
  }
  return weeks;
};

// Places each event overlapping the week into the first free lane, longest events first
const layoutWeek = (week: moment.Moment[], events: DayEvent[]) => {
  const weekStart = week[0];
  const weekEnd = week[6];

  const overlapping = events
    .filter((e) => !e.firstDay.isAfter(weekEnd) && !e.lastDay.isBefore(weekStart))
    .sort((a, b) =>
      a.firstDay.valueOf() - b.firstDay.valueOf()
      || b.lastDay.diff(b.firstDay) - a.lastDay.diff(a.firstDay));

  const laneEnds: number[] = [];
  const hiddenPerDay = Array(7).fill(0);
  const segments: Segment[] = [];

  overlapping.forEach((event) => {
    const startCol = Math.max(0, event.firstDay.diff(weekStart, 'days'));
    const endCol = Math.min(6, event.lastDay.diff(weekStart, 'days'));

    let lane = laneEnds.findIndex((end) => end < startCol);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = endCol;

    if (lane >= MAX_LANES) {
      for (let col = startCol; col <= endCol; col++) hiddenPerDay[col]++;
      return;
    }

    segments.push({
      event,
      startCol,
      span: endCol - startCol + 1,
      lane,
      continuesBefore: event.firstDay.isBefore(weekStart),
      continuesAfter: event.lastDay.isAfter(weekEnd),
    });
  });

  return { segments, hiddenPerDay };
};

const EventTooltip: React.FC<{ event: CalendarEvent }> = ({ event }) => (
  <Box sx={{ py: 0.25 }}>
    <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{event.name}</Typography>
    <Typography sx={{ fontSize: '12px', color: event.color, fontWeight: 600 }}>{event.statusLabel}</Typography>
    <Typography sx={{ fontSize: '12px', opacity: 0.8, ...numericSx }}>{formatRange(event)}</Typography>
  </Box>
);

const EventCalendar: React.FC<EventCalendarProps> = ({ events, legend }) => {
  const today = moment.utc().startOf('day');
  const [month, setMonth] = useState(() => today.clone().startOf('month'));
  const [selectedDay, setSelectedDay] = useState(() => today.clone());

  const dayEvents = useMemo(() => events.map(toDayEvent), [events]);
  const weeks = useMemo(() => buildWeeks(month), [month]);
  const layouts = useMemo(() => weeks.map((week) => layoutWeek(week, dayEvents)), [weeks, dayEvents]);

  const monthStart = month.clone().startOf('month');
  const monthEnd = month.clone().endOf('month');
  const eventsThisMonth = dayEvents.filter((e) => !e.firstDay.isAfter(monthEnd) && !e.lastDay.isBefore(monthStart)).length;

  const selectedEvents = dayEvents
    .filter((e) => !e.firstDay.isAfter(selectedDay) && !e.lastDay.isBefore(selectedDay))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  const changeMonth = (delta: number) => setMonth((current) => current.clone().add(delta, 'month'));
  const goToToday = () => {
    setMonth(today.clone().startOf('month'));
    setSelectedDay(today.clone());
  };
  const selectDay = (day: moment.Moment) => {
    setSelectedDay(day.clone());
    if (!day.isSame(month, 'month')) setMonth(day.clone().startOf('month'));
  };

  const isViewingToday = month.isSame(today, 'month');

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '20px', fontWeight: 800, letterSpacing: '-0.01em', color: DS.text, lineHeight: 1.2 }}>
            {month.format('MMMM YYYY')}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: DS.textMuted, mt: 0.25 }}>
            {eventsThisMonth === 0 ? 'No events this month' : `${eventsThisMonth} event${eventsThisMonth === 1 ? '' : 's'} this month`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            onClick={goToToday}
            disabled={isViewingToday && selectedDay.isSame(today, 'day')}
            sx={{
              mr: 0.5,
              color: DS.text,
              border: `1px solid ${DS.borderStrong}`,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 1.5,
            }}
          >
            Today
          </Button>
          <Tooltip title="Previous month">
            <IconButton size="small" onClick={() => changeMonth(-1)} sx={{ color: DS.textMuted, border: `1px solid ${DS.border}`, borderRadius: '10px' }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Next month">
            <IconButton size="small" onClick={() => changeMonth(1)} sx={{ color: DS.textMuted, border: `1px solid ${DS.border}`, borderRadius: '10px' }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Month grid */}
      <Box sx={{ border: `1px solid ${DS.border}`, borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', background: DS.inset, borderBottom: `1px solid ${DS.border}` }}>
          {WEEKDAYS.map((day) => (
            <Box key={day} sx={{ py: 1, textAlign: 'center' }}>
              <Overline>{day}</Overline>
            </Box>
          ))}
        </Box>

        {weeks.map((week, weekIndex) => {
          const { segments, hiddenPerDay } = layouts[weekIndex];
          return (
            <Box
              key={week[0].valueOf()}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gridTemplateRows: `${DAY_HEADER_HEIGHT}px repeat(${MAX_LANES}, ${LANE_HEIGHT}px) ${MORE_ROW_HEIGHT}px`,
                rowGap: '2px',
                borderTop: weekIndex === 0 ? 'none' : `1px solid ${DS.border}`,
              }}
            >
              {/* Day cells span the whole row and sit behind the event bars */}
              {week.map((day, col) => {
                const isToday = day.isSame(today, 'day');
                const isSelected = day.isSame(selectedDay, 'day');
                const inMonth = day.isSame(month, 'month');
                return (
                  <Box
                    key={day.valueOf()}
                    role="button"
                    aria-label={day.format('dddd, MMMM D')}
                    aria-pressed={isSelected}
                    tabIndex={0}
                    onClick={() => selectDay(day)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectDay(day); } }}
                    sx={{
                      gridColumn: col + 1,
                      gridRow: '1 / -1',
                      borderLeft: col === 0 ? 'none' : `1px solid ${DS.border}`,
                      background: isSelected ? alpha(DS.brand, 0.08) : inMonth ? 'transparent' : 'rgba(0, 0, 0, 0.18)',
                      boxShadow: isSelected ? `inset 0 0 0 1px ${alpha(DS.brand, 0.5)}` : 'none',
                      cursor: 'pointer',
                      transition: 'background 0.15s ease',
                      outline: 'none',
                      '&:hover': { background: isSelected ? alpha(DS.brand, 0.12) : DS.inset },
                      '&:focus-visible': { boxShadow: `inset 0 0 0 2px ${DS.brand}` },
                      p: '6px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 22,
                        height: 22,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        fontSize: '12px',
                        fontWeight: isToday ? 800 : 600,
                        color: isToday ? '#fff' : inMonth ? DS.text : DS.textFaint,
                        background: isToday ? DS.brand : 'transparent',
                        ...numericSx,
                      }}
                    >
                      {day.date()}
                    </Box>
                  </Box>
                );
              })}

              {segments.map((segment) => {
                const { event } = segment;
                return (
                  <Tooltip key={`${event.id}-${weekIndex}`} title={<EventTooltip event={event} />} arrow>
                    <Box
                      role={event.onClick ? 'button' : undefined}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (event.onClick) event.onClick();
                        else selectDay(moment.max(event.firstDay, week[segment.startCol]));
                      }}
                      sx={{
                        gridColumn: `${segment.startCol + 1} / span ${segment.span}`,
                        gridRow: segment.lane + 2,
                        zIndex: 1,
                        mx: '4px',
                        ml: segment.continuesBefore ? 0 : '4px',
                        mr: segment.continuesAfter ? 0 : '4px',
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        minWidth: 0,
                        background: alpha(event.color, 0.18),
                        borderLeft: segment.continuesBefore ? 'none' : `3px solid ${event.color}`,
                        borderRadius: `${segment.continuesBefore ? 0 : 6}px ${segment.continuesAfter ? 0 : 6}px ${segment.continuesAfter ? 0 : 6}px ${segment.continuesBefore ? 0 : 6}px`,
                        cursor: 'pointer',
                        transition: 'background 0.15s ease',
                        '&:hover': { background: alpha(event.color, 0.3) },
                      }}
                    >
                      <Typography noWrap sx={{ fontSize: '11px', fontWeight: 600, color: DS.text, lineHeight: `${LANE_HEIGHT}px` }}>
                        {event.name}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}

              {hiddenPerDay.map((count, col) => count > 0 && (
                <Box
                  key={`more-${col}`}
                  onClick={(e) => { e.stopPropagation(); selectDay(week[col]); }}
                  sx={{ gridColumn: col + 1, gridRow: MAX_LANES + 2, zIndex: 1, px: 1, cursor: 'pointer' }}
                >
                  <Typography noWrap sx={{ fontSize: '11px', fontWeight: 600, color: DS.textMuted, '&:hover': { color: DS.text } }}>
                    +{count}<Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}> more</Box>
                  </Typography>
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1.5 }}>
        {legend.map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CircleIcon sx={{ fontSize: 10, color: item.color }} />
            <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>{item.label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Selected day agenda */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Overline>{selectedDay.isSame(today, 'day') ? 'Today' : selectedDay.format('dddd')}</Overline>
          <Typography sx={{ fontSize: '13px', color: DS.textMuted }}>{selectedDay.format('MMMM D, YYYY')}</Typography>
        </Box>

        {selectedEvents.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 2, borderRadius: '10px', background: DS.inset, color: DS.textMuted }}>
            <EventBusyIcon fontSize="small" sx={{ color: DS.textFaint }} />
            <Typography sx={{ fontSize: '13px' }}>No events on this day</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {selectedEvents.map((event) => (
              <Box
                key={event.id}
                onClick={event.onClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: '10px',
                  cursor: event.onClick ? 'pointer' : 'default',
                  transition: 'background 0.15s ease',
                  '&:hover': { background: DS.inset },
                }}
              >
                <Box sx={{ width: 4, alignSelf: 'stretch', borderRadius: '999px', background: event.color, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography noWrap sx={{ fontSize: '14px', fontWeight: 600, color: DS.text }}>{event.name}</Typography>
                  <Typography noWrap sx={{ fontSize: '12px', color: DS.textMuted, ...numericSx }}>{formatRange(event)}</Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: event.color, flexShrink: 0 }}>
                  {event.statusLabel}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EventCalendar;
